import {useMutation, useQuery} from '@apollo/client'
import {FormEvent, useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {EDIT_POLICY} from '../../../gql/mutations/policyMutation'
import {
  AllPolicyStatusesData,
  AllPolicyStatusesVars,
  IResUpdatePolicyApi,
} from '../../../types/policies'
import {parseId} from '../../../helpers'
import moment from 'moment'
import Select from 'react-select'
import {GET_ALL_POLICIES_STATUSES} from '../../../gql/queries/policiesQueries'

import {getBootstrapColorHex, ALL_PERMISSIONS, STATUS_POLICY} from '../../../helpers/consts'
import {
  translatedDocumentStatuses,
  translatedPaymentStatuses,
} from '../../../helpers/catalogMessages'
import {NodePolicy} from '../../../types/policiesApi'
import {useGetUser} from '../../../store/userStore'
import {hasPermission} from '../../../helpers'
import {DateInput} from 'app/tfi/components/DateInput'

interface IModalProps {
  node: NodePolicy | undefined
  isOpen: boolean
  onClose: () => void
  refetch: () => void
}

const M = defineMessages({
  title: {id: 'policies.list.quickUpdate.title', defaultMessage: 'Quick Policy Update'},
  fieldStatus: {id: 'policies.list.quickUpdate.field.status', defaultMessage: 'Status'},
  phSelectStatus: {id: 'policies.list.quickUpdate.phSelectStatus', defaultMessage: 'Type or select a status'},
  fieldDocStatus: {id: 'policies.list.quickUpdate.field.docStatus', defaultMessage: 'Documents status'},
  selectOne: {id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'},
  noPermDoc: {id: 'policies.list.quickUpdate.noPermDoc', defaultMessage: "You don't have permissions to modify document status"},
  docsDueDate: {id: 'policies.list.quickUpdate.docsDueDate', defaultMessage: 'Documents Due Date'},
  fieldPayStatus: {id: 'policies.list.quickUpdate.field.payStatus', defaultMessage: 'Payment status'},
  noPermPay: {id: 'policies.list.quickUpdate.noPermPay', defaultMessage: "You don't have permissions to modify payment status"},
  fieldMemberId: {id: 'policies.list.quickUpdate.field.memberId', defaultMessage: 'Member ID'},
  phMemberId: {id: 'policies.list.quickUpdate.phMemberId', defaultMessage: 'Enter Member ID'},
  fieldEffectiveDate: {id: 'policies.list.quickUpdate.field.effectiveDate', defaultMessage: 'Effective date'},
  commissionsParams: {id: 'policies.list.quickUpdate.commissionsParams', defaultMessage: 'Commissions Parameters'},
  commissionSwitch: {id: 'policies.list.quickUpdate.commissionSwitch', defaultMessage: 'Consider this policy for commission reports? {answer}'},
  answerYes: {id: 'policies.list.quickUpdate.answerYes', defaultMessage: '(YES)'},
  answerNo: {id: 'policies.list.quickUpdate.answerNo', defaultMessage: '(NO)'},
  saveChanges: {id: 'policies.list.quickUpdate.saveChanges', defaultMessage: 'Save Changes'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  toastStatusUpdated: {id: 'policies.list.quickUpdate.toast.statusUpdated', defaultMessage: 'Status updated successfully'},
  toastNoPolicy: {id: 'policies.list.quickUpdate.toast.noPolicy', defaultMessage: 'No policy data available to update'},
  toastDocsDueRequired: {id: 'policies.list.quickUpdate.toast.docsDueRequired', defaultMessage: 'Documents due date is required when document status is Pending and policy status is Completed'},
  canceledDate: {id: 'policies.detail.statuses.canceledDate', defaultMessage: 'Cancellation date'},
  canceledDateHint: {
    id: 'policies.detail.statuses.canceledDateHint',
    defaultMessage: 'Date the coverage actually ends. Defaults to the end of the current month.',
  },
  canceledDateRequired: {
    id: 'policies.detail.statuses.canceledDateRequired',
    defaultMessage: 'The cancellation date is required to set a canceled status',
  },
  effectiveDateLocked: {
    id: 'policies.detail.form.insurance.effectiveDateLocked',
    defaultMessage: 'Locked: the effective date of a completed ACA policy is managed by Operations.',
  },
  // Status options (frontend consts) reuse the filters.* catalog
  psChargeback: {id: 'policies.list.filters.opt.chargeback', defaultMessage: 'Chargeback'},
})

export const ModalQuickUpdates: React.FC<IModalProps> = ({node, isOpen, onClose, refetch}) => {
  const intl = useIntl()
  // Obtener el usuario actual, sus grupos y permisos
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const permissions = useGetUser((s) => s.user?.permissions)
  const isAsesor = userGroups.includes('Asesor')
  const canMarkAsCompleted =
    permissions && hasPermission(permissions, ALL_PERMISSIONS.MARK_COMPLETED_POLICY)

  // Si tiene el permiso MARK_COMPLETED_POLICY, puede editar todo sin restricciones
  const hasRestrictedAccess = isAsesor && !canMarkAsCompleted
  const isQuotePolicy = node?.type === 'QUOTE'


  const {data: dataPoliciesStatuses, loading: loadingPoliciesStatus} = useQuery<
    AllPolicyStatusesData,
    AllPolicyStatusesVars
  >(GET_ALL_POLICIES_STATUSES, {
    fetchPolicy: 'cache-first',
  })

  // Filtrar los status según el rol del usuario
  const allStatusOptions =
    dataPoliciesStatuses?.allPolicyStatuses.edges.map(({node}) => ({
      value: node.code,
      label: node.title,
      category: node.category,
      // El backend decide quién puede asignar cada estado (core.authz.policy_rules).
      assignable: node.assignable !== false,
      color: getBootstrapColorHex(node.color || 'gray'),
      icon: (
        <i
          className='bi bi-circle-fill'
          style={{color: getBootstrapColorHex(node.color || 'gray')}}
        ></i>
      ),
    })) || []

  // El selector muestra solo lo que el usuario puede asignar. La regla ya no vive acá.
  const statusOptions = allStatusOptions.filter((option) => option.assignable)

  const customSingleValue = ({data}: any) => (
    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
      {data.icon}
      {data.label}
    </div>
  )

  const customOption = (props: any) => {
    const {data, innerRef, innerProps} = props
    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{display: 'flex', alignItems: 'center', padding: 10}}
      >
        {data.icon}
        <span style={{marginLeft: 8}}>{data.label}</span>
      </div>
    )
  }

  const [formData, setFormData] = useState({
    statusPolicy: '',
    documentStatus: '',
    documentsDueDate: '',
    paymentStatus: '',
    memberId: '',
    effectiveDate: '',
    canceledDate: '',
    commissionPayable: false,
  })

  const [gqlCreateP, {data: dataUpdate, loading: loadingUpdate}] =
    useMutation<IResUpdatePolicyApi>(EDIT_POLICY)

  const selectedStatusCategory =
    allStatusOptions.find((option) => option.value === formData.statusPolicy)?.category || null
  const shouldShowDocumentsDueDate =
    formData.documentStatus === 'PENDING' || formData.documentStatus === 'PROCESSING'
  // Una poliza no puede quedar cancelada sin fecha: es el dato del que sale el tiempo
  // activo. Misma regla que en el modal de status del detalle.
  const isCanceledStatus = selectedStatusCategory === STATUS_POLICY.CANCELED
  const shouldRequireCanceledDate = isCanceledStatus && !hasRestrictedAccess
  const canEditEffectiveDate = node?.canEditEffectiveDate !== false
  const shouldRequireDocumentsDueDate =
    !hasRestrictedAccess &&
    formData.documentStatus === 'PENDING' &&
    selectedStatusCategory === STATUS_POLICY.COMPLETED

  useEffect(() => {
    if (!dataUpdate) return

    if (dataUpdate && dataUpdate.updatePolicy.success) {
      toast.success(intl.formatMessage(M.toastStatusUpdated))
      refetch()
      onClose()
    } else if (dataUpdate.updatePolicy.errors && dataUpdate.updatePolicy.errors.length > 0) {
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: dataUpdate.updatePolicy.errors[0].message}
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])

  const handleDateChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!node) {
      toast.error(intl.formatMessage(M.toastNoPolicy))
      return
    }

    const currentStatusPolicy = node.status || ''
    const currentDocumentStatus = node.stateDocs || ''
    const currentPaymentStatus = node.paymentStatus || ''
    const currentMemberId = node.memberId || ''
    const currentEffectiveDate = node.effectiveDate ? String(node.effectiveDate) : ''
    const currentDocumentsDueDate = node.documentsDueDate || ''
    const currentCommissionPayable = node.commissionPayable ?? false

    const statusPolicyChanged = !isQuotePolicy && formData.statusPolicy !== currentStatusPolicy
    const documentStatusChanged =
      !hasRestrictedAccess && formData.documentStatus !== currentDocumentStatus
    const documentsDueDateChanged =
      !hasRestrictedAccess && formData.documentsDueDate !== currentDocumentsDueDate
    const paymentStatusChanged =
      !hasRestrictedAccess && formData.paymentStatus !== currentPaymentStatus
    const memberIdChanged = formData.memberId !== currentMemberId
    const effectiveDateChanged = formData.effectiveDate !== currentEffectiveDate
    const commissionPayableChanged = formData.commissionPayable !== currentCommissionPayable
    const onlyCommissionFieldsChanged =
      (memberIdChanged || commissionPayableChanged) &&
      !statusPolicyChanged &&
      !documentStatusChanged &&
      !documentsDueDateChanged &&
      !paymentStatusChanged &&
      !effectiveDateChanged
    const shouldValidateDocumentsDueDate =
      shouldRequireDocumentsDueDate && !onlyCommissionFieldsChanged

    if (shouldValidateDocumentsDueDate && !formData.documentsDueDate) {
      toast.error(intl.formatMessage(M.toastDocsDueRequired))
      return
    }

    if (statusPolicyChanged && shouldRequireCanceledDate && !formData.canceledDate) {
      toast.error(intl.formatMessage(M.canceledDateRequired))
      return
    }

    const policyData: Record<string, any> = {
      ...(statusPolicyChanged ? {status: formData.statusPolicy} : {}),
      ...(documentStatusChanged ? {stateDocs: formData.documentStatus} : {}),
      ...(paymentStatusChanged ? {paymentStatus: formData.paymentStatus} : {}),
      ...(memberIdChanged && formData.memberId !== '' ? {memberId: formData.memberId} : {}),
      ...(effectiveDateChanged && formData.effectiveDate && canEditEffectiveDate
        ? {effectiveDate: formData.effectiveDate}
        : {}),
      ...(statusPolicyChanged && isCanceledStatus && !hasRestrictedAccess && formData.canceledDate
        ? {canceledDate: formData.canceledDate}
        : {}),
      ...(commissionPayableChanged ? {commissionPayable: formData.commissionPayable} : {}),
    }

    if (!hasRestrictedAccess && (documentStatusChanged || documentsDueDateChanged)) {
      policyData.documentsDueDate =
        formData.documentStatus === 'PENDING' || formData.documentStatus === 'PROCESSING'
          ? formData.documentsDueDate || null
          : null
    }

    gqlCreateP({
      variables: {
        id: parseId(node.id),
        policyData,
      },
    })
  }

  // const {data: policyData, loading: loadingPolicy} = useQuery(GET_ONE_POLICY_API, {
  //   variables: {id: parseId(id)},
  //   skip: !isOpen, // evita llamar la query si el modal no está abierto
  // })

  // console.log('🚀 policyData v1:', policyData)

  useEffect(() => {
    if (isOpen && node) {
      console.log('✅ Precargando formData con:', node)
      const p = node
      setFormData({
        statusPolicy: p.status || '',
        documentStatus: p.stateDocs || '',
        documentsDueDate: p.documentsDueDate || '',
        paymentStatus: p.paymentStatus || '',
        memberId: p.memberId || '',
        effectiveDate: p.effectiveDate ? String(p.effectiveDate) : '',
        canceledDate: p.canceledDate ? String(p.canceledDate) : '',
        commissionPayable: p.commissionPayable ?? false,
      })
    }

    if (!isOpen) {
      console.log('🔄 Modal cerrado, limpiando formData')
      // Limpiar al cerrar
      setFormData({
        statusPolicy: '',
        documentStatus: '',
        documentsDueDate: '',
        paymentStatus: '',
        memberId: '',
        effectiveDate: '',
        canceledDate: '',
        commissionPayable: false,
      })
    }
  }, [node, isOpen])

  useEffect(() => {
    console.log('📦 formData actual:', formData)
  }, [formData])

  return (
    <Modal show={isOpen} onHide={onClose} className='modal-statuss-policy'>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(M.title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id='formId' onSubmit={handleSubmit}>
          {!isQuotePolicy && (
            <Form.Group>
              <Form.Label>{intl.formatMessage(M.fieldStatus)}</Form.Label>
              <Select
                className='selectable'
                options={statusOptions}
                value={statusOptions.find((opt) => opt.value === formData.statusPolicy)}
                onChange={(selected) => {
                  const nextCategory = allStatusOptions.find(
                    (opt) => opt.value === selected?.value
                  )?.category
                  setFormData((prev) => ({
                    ...prev,
                    statusPolicy: selected?.value || '',
                    // Sugerencia de fin de mes, editable.
                    canceledDate:
                      nextCategory === STATUS_POLICY.CANCELED && !prev.canceledDate
                        ? moment().endOf('month').format('YYYY-MM-DD')
                        : prev.canceledDate,
                  }))
                }}
                isSearchable={true}
                components={{SingleValue: customSingleValue, Option: customOption}}
                styles={{
                  control: (base) => ({...base, minHeight: 40}),
                  option: (base, {data, isFocused}) => ({
                    ...base,
                    backgroundColor: isFocused ? '#f0f0f0' : 'white',
                    color: data.color,
                  }),
                }}
                placeholder={intl.formatMessage(M.phSelectStatus)}
                isDisabled={loadingPoliciesStatus}
              />
            </Form.Group>
          )}
          <Form.Group className='mt-2'>
            <Form.Label>{intl.formatMessage(M.fieldDocStatus)}</Form.Label>
            <Form.Select
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  documentStatus: e.target.value,
                  documentsDueDate:
                    e.target.value === 'PENDING' || e.target.value === 'PROCESSING'
                      ? prev.documentsDueDate
                      : '',
                }))
              }
              value={formData.documentStatus ?? ''}
              disabled={hasRestrictedAccess}
            >
              <option value=''>{intl.formatMessage(M.selectOne)}</option>
              {translatedDocumentStatuses(intl).map(({value, label}) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Select>
            {hasRestrictedAccess && (
              <Form.Text className='text-muted'>
                {intl.formatMessage(M.noPermDoc)}
              </Form.Text>
            )}
          </Form.Group>
          {shouldShowDocumentsDueDate && (
            <Form.Group className='mt-2'>
              <DateInput
                id='documentsDueDate'
                name='documentsDueDate'
                label={intl.formatMessage(M.docsDueDate)}
                value={formData.documentsDueDate}
                onChange={handleDateChange}
                disabled={hasRestrictedAccess}
                required={shouldRequireDocumentsDueDate}
              />
            </Form.Group>
          )}
          <Form.Group className='mt-2'>
            <Form.Label>{intl.formatMessage(M.fieldPayStatus)}</Form.Label>
            <Form.Select
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  paymentStatus: e.target.value,
                }))
              }
              value={formData.paymentStatus ?? ''}
              disabled={hasRestrictedAccess}
            >
              <option value=''>{intl.formatMessage(M.selectOne)}</option>
              {translatedPaymentStatuses(intl).map(({value, label}) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
              <option value='CHARGEBACK'>{intl.formatMessage(M.psChargeback)}</option>
            </Form.Select>
            {hasRestrictedAccess && (
              <Form.Text className='text-muted'>
                {intl.formatMessage(M.noPermPay)}
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group className='mt-2'>
            <Form.Label>{intl.formatMessage(M.fieldMemberId)}</Form.Label>
            <Form.Control
              value={formData.memberId || ''}
              name='memberId'
              id='memberId'
              placeholder={intl.formatMessage(M.phMemberId)}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  memberId: e.target.value,
                }))
              }
            />
          </Form.Group>
          <Form.Group className='mt-2'>
            <Form.Label>{intl.formatMessage(M.fieldEffectiveDate)}</Form.Label>
            <Form.Control
              type='date'
              value={formData.effectiveDate}
              onChange={(e) => setFormData({...formData, effectiveDate: e.target.value})}
              disabled={!canEditEffectiveDate}
            />
            {!canEditEffectiveDate && (
              <Form.Text className='text-muted'>
                <i className='bi bi-lock-fill me-1' />
                {intl.formatMessage(M.effectiveDateLocked)}
              </Form.Text>
            )}
          </Form.Group>
          {isCanceledStatus && (
            <Form.Group className='mt-2'>
              <Form.Label>{intl.formatMessage(M.canceledDate)}</Form.Label>
              <Form.Control
                type='date'
                value={formData.canceledDate}
                onChange={(e) => setFormData({...formData, canceledDate: e.target.value})}
                disabled={hasRestrictedAccess}
                required={shouldRequireCanceledDate}
              />
              <Form.Text className='text-muted'>{intl.formatMessage(M.canceledDateHint)}</Form.Text>
            </Form.Group>
          )}
          <h6 className='mt-3'>{intl.formatMessage(M.commissionsParams)}</h6>
          <div className='commission-switch-wrapper'>
            <Form.Check
              label={intl.formatMessage(M.commissionSwitch, {
                answer: formData.commissionPayable
                  ? intl.formatMessage(M.answerYes)
                  : intl.formatMessage(M.answerNo),
              })}
              type='switch'
              name='commissionPayable'
              id='commissionPayable'
              checked={formData.commissionPayable}
              onChange={(e) => setFormData({...formData, commissionPayable: e.target.checked})}
            />
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' size='sm' onClick={onClose}>
          {intl.formatMessage(M.cancel)}
        </Button>
        <Button
          size='sm'
          variant='primary'
          type='submit'
          form='formId'
          formAction='formId'
          disabled={loadingUpdate}
        >
          {loadingUpdate && <Spinner animation='border' size='sm' className='me-2' />}
          {intl.formatMessage(M.saveChanges)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
